import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CreditCard, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { usePublicAuth } from '../context/PublicAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg";

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/mi-cuenta';
  const { user, loading, completeProfile, needsProfileCompletion } = usePublicAuth();
  
  const [dni, setDni] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Redirect if not authenticated or profile already complete
  useEffect(() => {
    if (!loading && !user) {
      navigate('/cuenta');
    }
    if (!loading && user && !needsProfileCompletion) {
      navigate(redirectUrl);
    }
  }, [loading, user, needsProfileCompletion, navigate, redirectUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dni || !birthDate) {
      toast.error('Completa todos los campos');
      return;
    }
    
    // Validate DNI
    const dniClean = dni.replace(/\D/g, '');
    if (dniClean.length < 7 || dniClean.length > 8) {
      toast.error('DNI inválido. Debe tener 7 u 8 dígitos.');
      return;
    }
    
    // Validate age
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    if (age < 18) {
      toast.error('Debes ser mayor de 18 años');
      return;
    }
    
    setSubmitting(true);
    try {
      await completeProfile(dniClean, birthDate);
      toast.success('¡Perfil completado!');
      navigate(redirectUrl);
    } catch (error) {
      console.error('Complete profile error:', error);
      toast.error(error.response?.data?.detail || 'Error al completar perfil');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#A1A1AA]" size={32} />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <section className="pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo */}
            <div className="text-center mb-8">
              <Link to="/">
                <img src={LOGO_URL} alt="Bravo Producciones" className="h-16 w-auto mx-auto mb-6" />
              </Link>
            </div>

            {/* Card */}
            <div className="bg-[#121212] border border-[#27272A] p-8" data-testid="complete-profile-card">
              <div className="flex items-center gap-3 mb-6 p-4 bg-[#27272A]/50 border border-[#27272A]">
                <AlertCircle size={20} className="text-yellow-500 flex-shrink-0" />
                <p className="text-sm text-[#A1A1AA]">
                  Para completar tu registro necesitamos algunos datos adicionales
                </p>
              </div>

              <h2 className="text-2xl font-black uppercase tracking-tight mb-2">
                Completar Perfil
              </h2>
              <p className="text-[#A1A1AA] text-sm mb-6">
                ¡Hola <span className="text-white">{user.full_name}</span>! Ingresa tu DNI y fecha de nacimiento para continuar.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                    DNI
                  </Label>
                  <div className="relative">
                    <CreditCard size={18} className="input-icon" />
                    <Input
                      type="text"
                      value={dni}
                      onChange={(e) => setDni(e.target.value.replace(/\D/g, ''))}
                      placeholder="12345678"
                      className="input-with-icon"
                      maxLength={8}
                      data-testid="complete-dni"
                    />
                  </div>
                  <p className="text-xs text-[#52525B] mt-1">
                    Sin puntos ni guiones
                  </p>
                </div>

                <div>
                  <Label className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                    Fecha de Nacimiento
                  </Label>
                  <div className="relative">
                    <Calendar size={18} className="input-icon" />
                    <Input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      className="input-with-icon"
                      data-testid="complete-birthdate"
                    />
                  </div>
                  <p className="text-xs text-[#52525B] mt-1">
                    Debes ser mayor de 18 años
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full"
                  data-testid="complete-profile-submit"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    'Completar Registro'
                  )}
                </Button>
              </form>
            </div>

            <p className="text-center text-[#52525B] text-xs mt-6">
              Estos datos son necesarios para verificar tu identidad y cumplir con regulaciones locales.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompleteProfilePage;
